import { visionLabApi } from '@/app/apiClient';
import {
  type CreateDeliveryInput,
  type CreateEntryInput,
  type CustomerSyncStatus,
  type Delivery,
  type DeliveryCompletionPreview,
  type DeliveryEntry,
  type DeliveryEntryAssetState,
  type DeliveryEntryBuildState,
  type DeliveryEntryOutput,
  type DeliveryEntryOutputOrphanReport,
  type DeliveryEntryPage,
  type DeliveryMetadataBinding,
  type DeliveryPage,
  type DeliverySummary,
  type EntryState,
  type EntryTask,
  type MetadataBindingReceipt,
  type MetadataSource,
  type PutMetadataBindingInput,
  type RebuildEntryInput,
  type UpdateMcapBuildConfigInput,
  createDeliveryInputSchema,
  createEntryInputSchema,
  customerSyncStatusInputSchema,
  putMetadataBindingInputSchema,
  reasonInputSchema,
  rebuildEntryInputSchema,
  updateMcapBuildConfigInputSchema,
} from '@/features/delivery/types/contracts';
import type { ApiCallOptions } from '@/shared/api/client';

/**
 * Transport-only bindings for the v2 customer Delivery routes
 * (`/deliveries…`, see `DeliveryCoreRoutes.lean`). Request bodies are validated
 * before transport; successful responses use the endpoint DTOs. No React or query
 * orchestration — those live in `services/`.
 */

const enc = encodeURIComponent;

export async function fetchDelivery(
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<Delivery> {
  return visionLabApi.get<Delivery>(`/deliveries/${enc(deliveryId)}`, options);
}

export async function setDeliveryCustomerSyncStatus(
  deliveryId: string,
  status: CustomerSyncStatus,
  options: ApiCallOptions = {},
): Promise<{ status: CustomerSyncStatus }> {
  const body = customerSyncStatusInputSchema.parse({ status });
  return visionLabApi.put<{ status: CustomerSyncStatus }>(
    `/deliveries/${enc(deliveryId)}/customer-sync-status`,
    { ...options, json: body },
  );
}

export interface FetchDeliveriesParams {
  limit?: number;
  cursor?: string | null;
  /**
   * Free-text filter, matched by the backend case-insensitively against the
   * delivery's **display name only** — not its id, and not its description. A
   * term that looks like a delivery id therefore matches nothing.
   */
  q?: string;
}

/**
 * List deliveries (`GET /deliveries`) — keyset-paginated and `.manage`-gated.
 * `cursor` is the previous page's last `deliveryId`; omit both params for the
 * first page (backend applies its default limit). Ordered by `deliveryId`, not
 * recency; `?q=` narrows the same ordering and pages by the same cursor.
 */
export async function fetchDeliveries(
  { limit, cursor, q }: FetchDeliveriesParams = {},
  options: ApiCallOptions = {},
): Promise<DeliveryPage> {
  return visionLabApi.get<DeliveryPage>('/deliveries', {
    ...options,
    searchParams: { limit, cursor: cursor || undefined, q: q || undefined },
  });
}

export async function createDelivery(
  input: CreateDeliveryInput,
  options: ApiCallOptions = {},
): Promise<Delivery> {
  const body = createDeliveryInputSchema.parse(input);
  return visionLabApi.post<Delivery>('/deliveries', { ...options, json: body });
}

export async function activateDelivery(
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<Delivery> {
  return visionLabApi.post<Delivery>(`/deliveries/${enc(deliveryId)}/activate`, { ...options });
}

export async function fetchDeliveryCompletionPreview(
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<DeliveryCompletionPreview> {
  return visionLabApi.get<DeliveryCompletionPreview>(
    `/deliveries/${enc(deliveryId)}/completion-preview`,
    options,
  );
}

export async function updateMcapBuildConfig(
  deliveryId: string,
  input: UpdateMcapBuildConfigInput,
  options: ApiCallOptions = {},
): Promise<Delivery> {
  const body = updateMcapBuildConfigInputSchema.parse(input);
  return visionLabApi.patch<Delivery>(`/deliveries/${enc(deliveryId)}/build-config`, {
    ...options,
    json: body,
  });
}

export async function cancelDelivery(
  deliveryId: string,
  reason: string,
  options: ApiCallOptions = {},
): Promise<Delivery> {
  const body = reasonInputSchema.parse({ reason });
  return visionLabApi.post<Delivery>(`/deliveries/${enc(deliveryId)}/cancel`, {
    ...options,
    json: body,
  });
}

export async function completeDelivery(
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<Delivery> {
  return visionLabApi.post<Delivery>(`/deliveries/${enc(deliveryId)}/complete`, { ...options });
}

export async function fetchDeliverySummary(
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<DeliverySummary> {
  return visionLabApi.get<DeliverySummary>(`/deliveries/${enc(deliveryId)}/summary`, options);
}

export interface ListDeliveryEntriesParams {
  /** Narrow to one derived batch. Omit for the whole delivery, in batch order. */
  batchNo?: number;
  /** Narrow to live or voided entries. Omit for both. */
  state?: EntryState;
  /** Narrow by the current generation's customer-visible build state. */
  buildState?: DeliveryEntryBuildState;
  /** Narrow by the current rejection state of the Entry's source Asset. */
  assetState?: DeliveryEntryAssetState;
  /**
   * Free-text filter the backend applies as a case-insensitive literal substring
   * match over FOUR readable columns: the entry key (`ENT-…`), the segment key
   * (`CLP-…`), the asset key (`FTG-…`), and the source filename. The opaque ids are
   * deliberately not searched.
   */
  q?: string;
  limit?: number;
  cursor?: string | null;
}

/**
 * One keyset page of a delivery's entries (`GET /deliveries/:id/entries`), ordered
 * by `(batchNo, ordinal)` — real batch order. `cursor` is the previous page's
 * opaque `nextCursor`; omit it for the first page.
 *
 * Every filter is **fail-closed** on the backend: a present-but-invalid `batchNo`,
 * `state`, `buildState`, `assetState`, or `cursor` is a `400`, never a silently wider page. A caller must
 * therefore never "recover" from that 400 by retrying without the parameter — that
 * would answer a narrowed question with the unnarrowed listing. A blank/whitespace
 * `q` is treated as no filter (omitted from the URL) so the cache key for "empty
 * search" matches the unfiltered list.
 */
export async function fetchDeliveryEntries(
  deliveryId: string,
  { batchNo, state, buildState, assetState, q, limit, cursor }: ListDeliveryEntriesParams = {},
  options: ApiCallOptions = {},
): Promise<DeliveryEntryPage> {
  const trimmed = q?.trim();
  return visionLabApi.get<DeliveryEntryPage>(`/deliveries/${enc(deliveryId)}/entries`, {
    ...options,
    searchParams: {
      batchNo,
      state,
      buildState,
      assetState,
      q: trimmed || undefined,
      limit,
      cursor: cursor || undefined,
    },
  });
}

/**
 * Add one segment to a delivery (`POST /deliveries/:id/entries`).
 *
 * There is no batch in the path and none in the body: the server derives placement
 * under the delivery row lock and returns the `batchNo`/`ordinal` it assigned. The
 * caller's only choice is the minted `entryId`, which is what makes replaying a
 * lost response idempotent.
 */
export async function createDeliveryEntry(
  deliveryId: string,
  input: CreateEntryInput,
  options: ApiCallOptions = {},
): Promise<DeliveryEntry> {
  const body = createEntryInputSchema.parse(input);
  return visionLabApi.post<DeliveryEntry>(`/deliveries/${enc(deliveryId)}/entries`, {
    ...options,
    json: body,
  });
}

export async function rebuildDeliveryEntry(
  entryId: string,
  input: RebuildEntryInput,
  options: ApiCallOptions = {},
): Promise<DeliveryEntry> {
  const body = rebuildEntryInputSchema.parse(input);
  return visionLabApi.post<DeliveryEntry>(`/deliveries/entries/${enc(entryId)}/rebuild`, {
    ...options,
    json: body,
  });
}

export async function voidDeliveryEntry(
  entryId: string,
  reason: string,
  options: ApiCallOptions = {},
): Promise<DeliveryEntry> {
  const body = reasonInputSchema.parse({ reason });
  return visionLabApi.post<DeliveryEntry>(`/deliveries/entries/${enc(entryId)}/void`, {
    ...options,
    json: body,
  });
}

export async function fetchEntryOutputs(
  entryId: string,
  options: ApiCallOptions = {},
): Promise<DeliveryEntryOutput[]> {
  return visionLabApi.get<DeliveryEntryOutput[]>(
    `/deliveries/entries/${enc(entryId)}/outputs`,
    options,
  );
}

/**
 * The SOURCE-side orphaned-output report
 * (`GET /deliveries/:id/entries/outputs/orphans`). Admin-only (`.manage`).
 *
 * READ-ONLY, and not merely by convention: there is no purge counterpart, because the
 * upload bucket the source renders live in grants the service no delete authority. So
 * no caller may offer a delete affordance over these rows.
 *
 * NOT paged — `?limit=` only, no cursor. What comes back is the first `limit`
 * candidates and there is nothing to page with, so the limit is the whole coverage
 * bound and a truncated report must read as truncated rather than as "all of them".
 *
 * The response has two independent sections plus `complete`:
 * - `items` — candidate leaked source objects, same per-row shape as the output audit.
 * - `cascadeGaps` — output entries the void cascade did not reach. These are the
 *   deliverables the PACKAGE-side audit will never look at, which is why they ride
 *   along here. A `building` row is an expected transient (the cascade deliberately
 *   leaves it alone to preserve `current_task_id`).
 * - `complete: false` — at least one object could not be probed, so this is not a
 *   complete leak list.
 */
export async function fetchDeliveryEntryOutputOrphans(
  deliveryId: string,
  { limit }: { limit?: number } = {},
  options: ApiCallOptions = {},
): Promise<DeliveryEntryOutputOrphanReport> {
  return visionLabApi.get<DeliveryEntryOutputOrphanReport>(
    `/deliveries/${enc(deliveryId)}/entries/outputs/orphans`,
    {
      ...options,
      searchParams: { limit },
    },
  );
}

/**
 * Read a delivery entry's build task (`GET /deliveries/entries/:id/task`). The
 * backend derives the `taskId` itself (`ownerDomain=delivery_entry`) — the
 * client never passes it. A 404 means there is no build task for the entry's
 * current generation yet (a normal, expected state, e.g. before the first
 * build). The service layer maps that 404 to `null`. Surfaces
 * the failure reason (`latestFailureReason` / `error` / `errorOutput`) when the
 * task has failed.
 */
export async function fetchDeliveryEntryTask(
  entryId: string,
  options: ApiCallOptions = {},
): Promise<EntryTask> {
  return visionLabApi.get<EntryTask>(`/deliveries/entries/${enc(entryId)}/task`, options);
}

/**
 * Read a delivery's ACTIVE metadata binding (`GET /deliveries/:id/metadata-binding`).
 * The backend 404s when the delivery has no binding yet. The service layer maps
 * that expected response to `null`.
 */
export async function fetchDeliveryMetadataBinding(
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<DeliveryMetadataBinding> {
  return visionLabApi.get<DeliveryMetadataBinding>(
    `/deliveries/${enc(deliveryId)}/metadata-binding`,
    options,
  );
}

/**
 * Replace a delivery's metadata binding (`PUT /deliveries/:id/metadata-binding`,
 * 201). Each accepted body appends a new immutable revision — there is no PATCH
 * and no POST, so an "edit" re-posts the complete contract.
 *
 * A rejection is NOT a transport problem and is deliberately not swallowed here:
 * 422 carries the backend's typed validation reason (an unknown/withheld/
 * unavailable source path, a mapping key outside the schema, a duplicated source),
 * 409 means a concurrent revision won the race, and both are mapped for the
 * operator by `lib/bindingErrors.ts`.
 *
 * The 201 body is an audit receipt, not the stored contract (see
 * {@link MetadataBindingReceipt}) — the caller re-reads the binding to see
 * what was persisted.
 */
export async function putDeliveryMetadataBinding(
  deliveryId: string,
  input: PutMetadataBindingInput,
  options: ApiCallOptions = {},
): Promise<MetadataBindingReceipt> {
  const body = putMetadataBindingInputSchema.parse(input);
  return visionLabApi.put<MetadataBindingReceipt>(
    `/deliveries/${enc(deliveryId)}/metadata-binding`,
    {
      ...options,
      json: body,
    },
  );
}

/**
 * The source-path catalog a metadata mapping draws from (`GET /metadata/sources`).
 * Carries no asset data — it is the vocabulary itself: every
 * `<namespace>.<leaf>` path with its availability and deliverability. Internal-only
 * paths are included (with their `withheldReason`) so the picker can show the whole
 * vocabulary and say why a path is refused.
 */
export async function fetchMetadataSources(
  options: ApiCallOptions = {},
): Promise<{ sources: MetadataSource[] }> {
  return visionLabApi.get<{ sources: MetadataSource[] }>('/metadata/sources', options);
}
